import { useCallback, useEffect, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { GET_VENUES } from '../../graphql/venues';
import useBehaviorSubjectState from '../rxjs/useSubjectState';
import { networkStatusSubject } from '../connectivity/useNetworkListener';
import { Venue } from '@oolio-group/domain';

export const useVenues = () => {
  const { value: isOnline } = useBehaviorSubjectState(networkStatusSubject);
  const hasFetchedRef = useRef(false);

  const [fetchVenues, { data, loading, error, refetch }] = useLazyQuery<{
    venues: Venue[];
  }>(GET_VENUES, {
    fetchPolicy: 'no-cache',
  });

  // When orgId is provided, it overrides the `organization` Apollo context
  // header the auth link would otherwise inject from the persisted session.
  // Use this from flows that select an org before committing it to session
  // (e.g. device-selection org switch). Without it, the request relies on
  // session.currentOrganization being committed before fire time.
  const getVenues = useCallback(
    (orgId?: string) =>
      fetchVenues(
        orgId ? { context: { headers: { organization: orgId } } } : undefined,
      ),
    [fetchVenues],
  );

  useEffect(() => {
    // reset the fetch flag when going offline so we refetch on reconnect
    if (!isOnline) {
      hasFetchedRef.current = false;
      return;
    }

    // only fetch when online, not already loading, and not yet fetched
    if (
      isOnline &&
      !loading &&
      !hasFetchedRef.current &&
      !data?.venues?.length
    ) {
      hasFetchedRef.current = true;
      fetchVenues();
    }
  }, [data?.venues?.length, fetchVenues, isOnline, loading]);

  // Clear stale errors when loading to prevent navigation issues
  // If we're loading, we don't want old errors to affect screen determination
  const activeError = loading ? null : error;

  return {
    venues: data?.venues ?? [],
    loading,
    error: activeError,
    refetch,
    getVenues,
  };
};
